"use client";

import React, { useEffect, useState, useRef } from "react";
import { PageBlogPostSchema } from "@/sanity/pages/PageBlogPostTemplate";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Header from "@/components/global/partials/Header";
import Footer from "@/components/global/partials/Footer";
import { GroqImage } from "@/helpers/Image";
import Image from "next/image";
// import calendar from "@/public/global/calendar.svg";
import { CTAButton } from "@/shadcn/ui/button";
import footerLogo from "@/public/global/footer-logo.svg";
import { SanityVideoPlayer } from "../ui/SanityVideoPlayer";
import { AnimatePresence, motion } from "framer-motion";
import NavBarV2 from "../global/partials/HeaderV2";
import { useDateTimeFormat } from "sanity";
import FormattedDate from "../global/countdown/FormattedDate";
import { resolvePageLink } from "@/sanity/globals/CTA";
import LastCTASection from "../global/sections/LastCTA";
import Link from "next/link";
import { cn } from "@/lib/utils";
interface Heading {
  text: string;
  id: string;
}

export default function PageBlogPostJSX({
  data,
}: {
  data: PageBlogPostSchema;
}) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeHeading, setActiveHeading] = useState<string>("");
  const contentRef = useRef<HTMLDivElement>(null);

  // Extract headings on component mount
  useEffect(() => {
    if (!data.content || !Array.isArray(data.content)) return;

    const extractedHeadings = data.content
      .filter(
        (block: any) =>
          block._type === "block" &&
          block.style === "h2" &&
          Array.isArray(block.children) &&
          block.children.length > 0,
      )
      .map((heading: any) => {
        // Extract heading text with proper type checking
        const text = Array.isArray(heading.children)
          ? heading.children.map((child: any) => child?.text || "").join("")
          : "";

        const trimmedText = text.trim();

        // Generate ID from text
        const id = trimmedText
          .toLowerCase()
          .replace(/[^\w\s]/g, "")
          .replace(/\s+/g, "-");

        return { text: trimmedText, id };
      })
      .filter((heading) => heading.text !== "");

    setHeadings(extractedHeadings);
  }, [data.content]);

  // Add IDs to h2 elements after content is rendered
  // Fixed dependency array to remove contentRef.current
  useEffect(() => {
    if (!contentRef.current || headings.length === 0) return;

    const h2Elements = contentRef.current.querySelectorAll("h2");

    headings.forEach((heading) => {
      h2Elements.forEach((element) => {
        if (element.textContent === heading.text) {
          element.id = heading.id;
        }
      });
    });
  }, [headings]);

  // Add intersection observer for active heading detection
  useEffect(() => {
    if (!contentRef.current || headings.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: "-100px 0px -70% 0px",
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveHeading(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    const h2Elements = contentRef.current.querySelectorAll("h2[id]");
    h2Elements.forEach((element) => observer.observe(element));

    return () => {
      h2Elements.forEach((element) => observer.unobserve(element));
    };
  }, [headings]);

  // Add smooth scrolling for TOC links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      e.preventDefault();

      const target = e.currentTarget as HTMLAnchorElement;
      const href = target.getAttribute("href");
      if (!href) return;

      const headingId = href.substring(1);
      setActiveHeading(headingId);

      const targetElement = document.querySelector(href);
      if (!targetElement) return;

      // Fixed offsetTop not existing on Element
      const rect = targetElement.getBoundingClientRect();
      window.scrollTo({
        top: rect.top + window.scrollY - 100, // Offset for header
        behavior: "smooth",
      });

      // Update URL without scroll
      window.history.pushState(null, "", href);
    };

    const anchors =
      document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]');
    anchors.forEach((anchor) => {
      anchor.addEventListener("click", handleAnchorClick as EventListener);
    });

    return () => {
      anchors.forEach((anchor) => {
        anchor.removeEventListener("click", handleAnchorClick as EventListener);
      });
    };
  }, [headings]);

  // Helper function to extract plain text from PortableText blocks
  function extractTextFromPortableText(
    content: any[],
    maxLength: number = 200,
  ): string {
    if (!content || !Array.isArray(content)) return "";

    const textParts: string[] = [];

    for (const block of content) {
      if (block._type === "block" && block.children) {
        for (const child of block.children) {
          if (child.text) {
            textParts.push(child.text);
            // Check if we've reached the desired length
            if (textParts.join(" ").length >= maxLength) {
              break;
            }
          }
        }
        // Break outer loop if we have enough text
        if (textParts.join(" ").length >= maxLength) {
          break;
        }
      }
    }

    const fullText = textParts.join(" ");
    if (fullText.length > maxLength) {
      return fullText.substring(0, maxLength).trim() + "...";
    }
    return fullText;
  }

  return (
    <>
      {data?.settings.header && (
        <NavBarV2 headerData={data.settings.header} animate={true} />
      )}
      <section className="flex relative flex-col items-center lg:px-0 overflow-x-clip pt-[2rem] lg:pt-[5rem] pb-[1.5rem] px-[2rem] z-[2]">
        <div className="relative w-[min(230rem,100%)] overflow-clip lg:overflow-visible px-[0rem] pt-[13rem] lg:pt-[18.75rem] pb-[0rem] lg:pb-0">
          <div
            className={cn(
              "absolute z-0 top-0 left-0 w-full h-full lg:h-[75%] bg-gradient-to-b rounded-[2rem] lg:rounded-[5rem]",
              "from-[#FBFBFB] to-[#E6F2EB]",
            )}
          ></div>
          <div className="relative z-[1] w-[min(200.375rem,100%)] mx-auto flex flex-col items-center">
            {data?.tags && (
              <div className="flex items-center mx-auto gap-[2rem] mb-[3rem] px-[2rem] lg:px-0">
                {data?.tags?.map((item, index) => (
                  <div
                    key={"tag" + index}
                    className="rounded-full bg-[#85D0A6] py-[1.38rem] px-[1.25rem] w-fit"
                  >
                    <p className="m-0 text-[#103037] font-[400] text-[1.75rem] leading-[100%] capitalize tracking-[-0.04rem]">
                      {item}
                    </p>
                  </div>
                ))}

                {data.pubDate && (
                  <div className="text-[#103037]/50 leading-[100%] text-[2.25rem] flex justify-center text-center h-fit [&_time]:h-fit">
                    <FormattedDate
                      dateTimeString={data.pubDate}
                      format="LLLL d, yy"
                    />{" "}
                  </div>
                )}
              </div>
            )}

            <div className="mb-[1.5rem] px-[2rem] lg:px-0">
              {data?.title && (
                <h1 className="w-[min(94.25rem,100%)] mx-auto text-[4rem] lg:text-[6rem] tracking-[-0.08rem] lg:tracking-[-0.18rem] font-[500] text-[#103037] font-geist leading-[120%] text-center">
                  {data?.title}
                </h1>
              )}
            </div>

            <div className="!w-full rounded-[1.5rem] lg:!w-[176.375rem] overflow-clip">
              {data.toggleVideoBanner && data.video ? (
                <>
                  <SanityVideoPlayer
                    playerData={data.video}
                    playsinline={true}
                    className={cn(
                      "!w-full rounded-[1.5rem] lg:!w-[176.375rem]",
                    )}
                  />
                </>
              ) : (
                <>
                  {data.banner && (
                    <Image
                      src={data.banner}
                      alt=""
                      className="!w-full rounded-[1.5rem]"
                    />
                  )}
                </>
              )}
            </div>

            {/*<div className="flex items-center gap-[3rem]">
              {data?.author && data?.author?.image && (
                <div className="flex items-center gap-[1.75rem]">
                  <GroqImage
                    src={data.author.image}
                    alt=""
                    className="flex-shrink-0 !w-[3.5rem]"
                  />
                  <div className="flex items-center gap-[.5rem]">
                    <p className="text-black/60 font-[500] text-[2rem] leading-[130%] font-dmsans m-0">
                      By
                    </p>
                    <p className="text-black font-[500] text-[2rem] leading-[130%] font-dmsans m-0">
                      {data?.author?.name}
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-black w-[.5rem] h-[.5rem]" />

              {data?.pubDate && (
                <div className="flex items-center gap-[1rem]">
                  <Image src={calendar} alt="" className="w-[3rem]" />
                  <p className="text-[2rem] text-black/50 m-0 font-[500] font-dmsans leading-[130%]">{`${new Date(data.pubDate).getDate()} ${new Date(data.pubDate).toLocaleDateString("en-GB", { month: "short" })}, ${new Date(data.pubDate).getFullYear()}`}</p>
                </div>
              )}
            </div>*/}
          </div>
        </div>
      </section>

      <section className="relative bg-white pt-[3.13rem] pb-[12rem] overflow-clip z-[2] lg:pt-[14.75rem]">
        <div className="w-[min(150rem,100%)] flex flex-col mx-auto">
          <div className="flex flex-col lg:flex-row justify-center gap-[12.5rem] w-[min(150rem,100%)] px-[3rem] lg:px-0">
            {/*<div className="relative w-[min(70rem,100%)] lg:w-[min(40.125rem,100%)] flex-shrink-0 lg:block hidden">
              <div className="relative bg-white lg:sticky lg:top-[12rem]">
                <p className="leading-[120%] uppercase font-display text-black/30 mb-[3.5rem] pb-[2rem] font-[600] lg:text-[1.75rem]">
                  Table of Content
                </p>

                <div className="flex flex-col items-start">
                  {headings.length > 0 && (
                    <ul className="flex flex-col w-full gap-[1.5rem] lg:gap-[2rem]">
                      {headings.map((heading) => (
                        <li key={heading.id}>
                          <a
                            href={`#${heading.id}`}
                            className={`flex items-start transition-all gap-[1.5rem] ${
                              activeHeading === heading.id
                                ? "text-black opacity-100"
                                : "text-black/60 opacity-80 hover:opacity-100"
                            }`}
                          >
                            <div
                              className={`flex-shrink-0 rounded-full w-[0.625rem] h-[0.625rem] translate-y-[.5rem] transition-all ${
                                activeHeading === heading.id
                                  ? "bg-[#7FA52C]"
                                  : "bg-[#000000]/50"
                              }`}
                            />

                            <span
                              className={`leading-[120%] font-display font-[500] lg:text-[2rem] m-0 transition-all ${
                                activeHeading === heading.id
                                  ? "text-black"
                                  : "text-black/60"
                              }`}
                            >
                              {heading.text.replace(/(?:[,:]| - ).*$/, "")}
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>*/}
            <div className="relative w-[min(70rem,100%)] lg:w-[min(7rem,100%)] flex-shrink-0 lg:block hidden">
              <div className="relative bg-white lg:sticky lg:top-[12rem]">
                <div className="flex flex-col items-start border border-[#1030371A]">
                  {data.socialsList && data.socialsList.length > 0 && (
                    <ul className="flex flex-col w-full">
                      {data.socialsList.map((item, index) => (
                        <li key={"social" + index}>
                          <a
                            href={resolvePageLink(item.link) ?? "/"}
                            target={item.link.openInNewTab ? "_blank" : "_self"}
                            className={`flex items-start transition-all gap-[1.5rem]  p-[2rem] ${index !== data.socialsList?.length! - 1 ? "border-b border-[#1030371A]" : ""}`}
                          >
                            <Image
                              src={item.icon}
                              alt=""
                              className="!w-[3rem] shrink-0"
                            />
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <div className="w-[min(101.75rem,100%)]" id="content">
              <div
                ref={contentRef}
                // prose-ul:border-l prose-ul:border-black/10 prose-ul:!pl-[5.5rem] prose-ul:mb-[3rem] prose-li:text-black prose-li:text-opacity-70
                className="grow !block richtext richtext-start
                [&_image]:!w-full [&_image]:!my-[3rem]
                lg:prose-h2:text-[4.5rem] prose-h2:font-geist prose-h2:text-[#2E3436] prose-h2:font-medium prose-headings:mb-[2rem] prose-headings:mt-[4rem] prose-h2:mt-[8rem]
                prose-p:text-[#1C1F22]/80 prose-p:text-[2.25rem] prose-strong:text-opacity-100 prose-h2:break-words"
              >
                {data?.content && (
                  <PortableText
                    value={data.content}
                    components={richTextComponent}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-clip lg:border-t lg:border-black/10">
        <div className="container flex flex-col items-center pt-[0rem] lg:pt-[15.25rem] pb-[7rem] lg:pb-[20rem] relative">
          {data?.recommendedTitle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="richtext sm:w-[45rem] lg:w-[min(55rem,100%)]"
            >
              <PortableText
                value={data.recommendedTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}

          <div className="mt-[2.5rem] lg:mt-[4.75rem] w-[min(175rem,100%)] grid grid-cols-1 lg:grid-cols-3 gap-[2.75rem] lg:gap-[5.13rem]">
            {data.recommendedBlogList &&
              data.recommendedBlogList.length > 0 &&
              data.recommendedBlogList.map((post, index) => (
                <AnimatePresence key={post._id}>
                  <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 0.3,
                        ease: [0.25, 0.46, 0.45, 0.94],
                      },
                    }}
                    viewport={{ once: true }}
                  >
                    <Link
                      key={post._id}
                      href={post.slug}
                      className="block border overflow-clip group rounded-[1.5rem] border-black/10"
                    >
                      <div className="relative h-full bg-white p-[2.25rem]">
                        {post.banner && (
                          <div className="relative overflow-clip aspect-[16/9] rounded-[1.5rem]">
                            <div className="absolute z-10 w-full h-full pointer-events-none p-[2rem]">
                              {post.tags && post.tags.length > 0 && (
                                <div className="flex flex-wrap pointer-events-auto gap-[1rem] mb-[2rem]">
                                  {post.tags.map((tag, tagIndex) => (
                                    <div
                                      key={tagIndex}
                                      className="rounded-full bg-[#85D0A6] py-[0.75rem] px-[1.25rem] w-fit"
                                    >
                                      <p className="m-0 text-[#103037] font-[400] text-[1.75rem] leading-[140%]">
                                        {tag}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <Image
                              src={post.banner}
                              alt={post.title}
                              className="object-cover !h-full group-hover:scale-105 transition-all duration-500 rounded-[1.5rem]"
                              quality={90}
                            />
                          </div>
                        )}
                        <div className="flex flex-col ">
                          <div className="mt-[3rem] mb-[1rem] text-[#103037]/50 leading-[100%] text-[2rem] flex h-fit [&_time]:h-fit">
                            {post.pubDate && (
                              <FormattedDate
                                dateTimeString={post.pubDate}
                                format="LLLL d, yy"
                              />
                            )}
                          </div>
                          <h3 className="text-[3rem] lg:text-[3rem] font-[500] text-black leading-[120%] line-clamp-2 mb-[1.5rem]">
                            {post.title}
                          </h3>
                          {post.content && (
                            <p className="text-black/60 text-[1.75rem] lg:text-[2rem] leading-[150%] line-clamp-3 mb-[1.5rem] m-0">
                              {extractTextFromPortableText(post.content, 200)}
                            </p>
                          )}

                          <div className="flex items-center gap-[1rem] text-black text-[2rem]">
                            Read more
                            <svg
                              className="w-[1rem]"
                              xmlns="http://www.w3.org/2000/svg"
                              width="8"
                              height="11"
                              viewBox="0 0 8 11"
                              fill="none"
                            >
                              <path
                                d="M0.668945 0.743164L5.66895 5.24316L0.668945 9.74316"
                                stroke="#6DA991"
                                strokeWidth="2"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                </AnimatePresence>
              ))}
          </div>
        </div>
      </section>

      <LastCTASection
        chip={data?.lastChip}
        text={data?.lastText}
        cta={data?.lastCTA}
      />

      <Footer data={data?.settings?.footer} />
    </>
  );
}
